import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, onBeforeMount, getCurrentInstance } from 'vue';
import { TVSystem } from './enums/tv-system';
import api from "./api";
import { useStore } from "@/composables/useStore";
import { RouteName } from '@/router/routes';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;
// Make route reactive by using computed
const route = computed(() => instance?.proxy?.$route);


const unscaledRoute = computed(() => route.value?.meta?.unscaled === true);
const loadScript = instance?.proxy?.$loadScript;

const { useGetter, store } = useStore();

const historyBack = (event: KeyboardEvent) => {
  if (event.defaultPrevented) {
    return;
  }

  const key = event.key || event.keyCode;
  if (key === 'Escape' || key === 'Esc' || key === 'BrowserBack' || key === 27) {
    navigateBack();
  }
};

const navigateBack = () => {
  const currentRoute = router?.currentRoute.name;
  console.log('Window: ', currentRoute, JSON.stringify(router?.currentRoute.query));

  const topLevelTabs: string[] = [
    RouteName.Discover,
    RouteName.Search,
    RouteName.Live,
    RouteName.Profile,
    RouteName.Settings
  ];

  if ((currentRoute === RouteName.Rails || currentRoute === RouteName.Welcome) && !router?.currentRoute.hash) {
    //If route is Welcome do nothing - Can't close apps progmatically
    window.close();
    return;
  } else if (currentRoute && topLevelTabs.indexOf(currentRoute) !== -1 && !router?.currentRoute.hash) {
    //If route if part of top nav go to welcome
    router?.push({ path: `/` });
  } else if (currentRoute === 'Player' && window.location.href.includes('deeplink=true')) {
    //For deep links for Virgin media we hijack back to bring the user home
    console.log("Going home");
    router?.replace({ path: `/` });
  } else {
    //Else go back
    router?.back();
  }
};

onBeforeMount(async () => {
  if (store.state.token !== "") {
    api.PeachAPI.setAuthToken(store.state.token);

    //Instead of hard auth we get user on app open so we no if access is revoked
    //No need to await
    store.dispatch("GET_USER").catch(err => {
      console.log("Get user error: ", err);
      api.PeachAPI.clearAuth();
      store.dispatch('LOGOUT');
    });
  }

  const currentUrl = window.location.href;
  console.log(`Current URL => ${currentUrl}`);

  // Registered before the lookup so the very first CMS response can correct it.
  store.dispatch('OBSERVE_API_LOCATION');

  //Likely no need to wait for this
  store.dispatch("GET_LIVE_CONFIG");

  //Check for Virgin media device using user agent
  // VM PC versions
  // EOSSTB, EOS2STB, APLSTB and APPSTB
  if (navigator.userAgent.indexOf("PC=EOSSTB") !== -1 || navigator.userAgent.indexOf("PC=EOS2STB") !== -1 || navigator.userAgent.indexOf("PC=APLSTB") !== -1 || navigator.userAgent.indexOf("PC=APPSTB") !== -1) {
    console.log("Virgin Media Found");
    instance?.proxy?.$recordEvent('using-virgin-media-box');
    store.dispatch('SET_USES_ONSCREEN_KEYBOARD', true);
    store.dispatch('SET_TV_SYSTEM', TVSystem.VIRGIN_MEDIA);
    document.addEventListener('keydown', historyBack);
    return;  //No need to continue
  }

  if (navigator.userAgent.indexOf("TiVoOS") !== -1) {
    console.log("TiVo Found");
    instance?.proxy?.$recordEvent('using-tivo-box');
    store.dispatch('SET_TV_SYSTEM', TVSystem.TIVO);
    document.addEventListener('keydown', historyBack);
    return;  //No need to continue
  }

  if (navigator.userAgent.indexOf("VESTEL") !== -1) {
    console.log("VESTEL Found");
    instance?.proxy?.$recordEvent('using-vestel');
    store.dispatch('SET_TV_SYSTEM', TVSystem.VESTEL);
    return;  //No need to continue
  }

  //Check for LG using scripts
  try {
    await loadScript?.("/js/webOSTV-dev.js");
    await loadScript?.("/js/webOSTV.js");
    const lgDeviceInfo = await new Promise<any>((resolve) => {
      const timeout = setTimeout(() => resolve({}), 3000);
      window.webOS.deviceInfo((info: any) => {
        clearTimeout(timeout);
        resolve(info);
      });
    });

    if (Object.keys(lgDeviceInfo).length > 0) {
      console.log("LG Found");
      store.dispatch('SET_TV_SYSTEM', TVSystem.LG);
      const majorVersion = lgDeviceInfo.sdkVersion?.split('.')?.[0];
      if (majorVersion) {
        store.dispatch('SET_PLATFORM_VERSION', `webOS ${majorVersion}`);
        store.dispatch('SET_PLATFORM_MAJOR_VERSION', parseInt(majorVersion, 10));
      }
      instance?.proxy?.$recordEvent('using-lg-tv');
      return; // no need to continue
    }
  } catch (err) {
    //Could't load scripts
    //Continue
  }

  try {
    await loadScript?.("$WEBAPIS/webapis/webapis.js");
    if (window.webapis) {
      console.log("Samsung Found");
      store.dispatch('SET_TV_SYSTEM', TVSystem.SAMSUNG);
      const tizenMatch = navigator.userAgent.match(/Tizen\s+(\d+)/);
      if (tizenMatch?.[1]) {
        store.dispatch('SET_PLATFORM_VERSION', `Tizen ${tizenMatch[1]}`);
        store.dispatch('SET_PLATFORM_MAJOR_VERSION', parseInt(tizenMatch[1], 10));
      }
      instance?.proxy?.$recordEvent('using-samsung-tv');

      document.addEventListener('keydown', (event) => {
        // back button for samsung
        if (event.defaultPrevented) {
          return;
        }
        if (event.keyCode === 10009) {
          navigateBack();
        }
      });

      return;  //No Need to continue
    }
  } catch (err) {
    // Continue
  }
});


// DEBUG LOG CAPTURE
const debugModeEnabled = useGetter<boolean>('debugModeEnabled');

/** The overlay renders every entry it holds, so the buffer is capped. */
const MAX_LOGS = 200;

const logs = ref<{ id: number; text: string }[]>([]);
let nextLogId = 0;

//Format log arguments for display in the debug panel
const formatLogArgs = (args: any[]): string => {
  return args
    .map((arg) => {
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON && JSON.stringify ? JSON.stringify(arg) : String(arg);
        } catch (err) {
          // Circular structures — DOM events, dash.js events, Vue instances —
          // make JSON.stringify throw, and the throw would come back out of
          // the console.log the app code was innocently making.
          return String(arg);
        }
      }
      return String(arg);
    })
    .join(' ');
};

/**
 * The one rule here: never touch `logs` from the console call itself.
 *
 * A log made while a computed or a render is being evaluated runs with that
 * watcher as Vue's active dependency target, so reading `logs` there
 * subscribes the watcher to the array the log is about to mutate — the
 * mutation marks it dirty, re-rendering re-runs it, and it logs again, with
 * nothing to stop it (a production Vue build strips its own circular-update
 * guard). Lines queue on a plain array and land in `logs` from a timeout,
 * where there is no dependency target to collect, which also batches a chatty
 * page down to one render.
 */
let pendingLogs: string[] = [];
let captureEnabled = false;

const captureLog = (args: any[]) => {
  if (!captureEnabled) return;

  pendingLogs.push(formatLogArgs(args));

  // Anything but the first line of a batch joins the flush already scheduled.
  if (pendingLogs.length > 1) return;

  setTimeout(() => {
    // Newest first, as the panel reads top-down.
    const batch = pendingLogs
      .splice(0)
      .reverse()
      .map((text) => ({ id: nextLogId++, text }));

    logs.value = batch.concat(logs.value).slice(0, MAX_LOGS);
  });
};

let consolePatched = false;
const startLogCapture = () => {
  if (consolePatched) return;
  consolePatched = true;

  const oldLog = console.log;
  console.log = (...args: any[]) => {
    oldLog.apply(console, args);
    captureLog(args);
  };

  const oldErrorLog = console.error;
  console.error = (...args: any[]) => {
    oldErrorLog.apply(console, args);
    captureLog(args);
  };
};

// The patch stays once applied; `captureEnabled` is what turns capture on and
// off, so switching debug mode back off stops the buffer filling behind the
// hidden overlay.
watch(debugModeEnabled, (enabled) => {
  captureEnabled = enabled;

  if (enabled) {
    startLogCapture();
  } else {
    pendingLogs = [];
    logs.value = [];
  }
}, { immediate: true });




return { __sfc: true,instance, router, route, unscaledRoute, loadScript, useGetter, store, historyBack, navigateBack, debugModeEnabled, MAX_LOGS, logs, nextLogId, formatLogArgs, pendingLogs, captureEnabled, captureLog, consolePatched, startLogCapture }
}

})